import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import socketIOClient from 'socket.io-client';
import './Dashboard.css'; // Import the CSS file
import { ReactComponent as ChatTradeLogo } from './ChatTradeLogo.svg'; // Import the SVG file
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import { createChart, CandlestickSeries } from 'lightweight-charts';
import ConfidenceBar, { ConfidenceComparison, DualConfidenceBar, FloatingConfidencePanel } from "./components/ConfidenceBar";

// Use environment variable for API endpoint
const ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "http://localhost:3003";

// Memoized MiniCandleChart component moved outside to prevent recreation
const MiniCandleChart = React.memo(({ ohlcv, asset }) => {
  const chartRef = useRef();
  const chartInstanceRef = useRef(null);
  const seriesRef = useRef(null);
  const isCreatedRef = useRef(false);

  useEffect(() => {
    // Only create chart if not already created and DOM element exists
    if (!isCreatedRef.current && chartRef.current) {
      isCreatedRef.current = true;
      
      try {
        chartInstanceRef.current = createChart(chartRef.current, {
          width: 400,
          height: 80,
          layout: { background: { color: 'transparent' } },
          timeScale: { visible: false },
          rightPriceScale: { visible: false },
          leftPriceScale: { visible: false },
          crosshair: { horzLine: { visible: false }, vertLine: { visible: false } },
          grid: { vertLines: { visible: false }, horzLines: { visible: false } }
        });
        
        seriesRef.current = chartInstanceRef.current.addSeries(CandlestickSeries, {
          upColor: '#4caf50',
          downColor: '#f44336',
          borderUpColor: '#4caf50',
          borderDownColor: '#f44336',
          wickUpColor: '#4caf50',
          wickDownColor: '#f44336',
        });
      } catch (error) {
        console.error(`Error creating chart for ${asset}:`, error);
        isCreatedRef.current = false;
      }
    }
    
    return () => {
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.remove();
        } catch (error) {
          console.error(`Error removing chart for ${asset}:`, error);
        }
        chartInstanceRef.current = null;
        seriesRef.current = null;
        isCreatedRef.current = false;
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Update chart data when ohlcv changes
  useEffect(() => {
    if (ohlcv && ohlcv.length > 0 && seriesRef.current && chartInstanceRef.current) {
      try {
        seriesRef.current.setData(ohlcv);
        chartInstanceRef.current.timeScale().fitContent();
      } catch (error) {
        console.error(`Error updating chart data for ${asset}:`, error);
      }
    }
  }, [ohlcv, asset]);

  return <div ref={chartRef} style={{ width: 300, height: 80, minWidth: 200 }} />;
});

const Dashboard = () => {
  const [status, setStatus] = useState({});
  const [positions, setPositions] = useState([]);
  const [accountInfo, setAccountInfo] = useState({});
  const [combinedData, setCombinedData] = useState({});
  const [performanceData, setPerformanceData] = useState({});
  const [aiStats, setAiStats] = useState({});
  const [signalAnalysis, setSignalAnalysis] = useState({});
  const [assetAnalysis, setAssetAnalysis] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [debugMode, setDebugMode] = useState(false);
  const [showFloatingPanel, setShowFloatingPanel] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    const socket = socketIOClient(ENDPOINT, {
      transports: ['polling'],
      timeout: 20000,
      forceNew: true,
      withCredentials: false
    });

    socket.on('connect', () => {
      console.log('Socket connected successfully');
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      console.error('Error details:', {
        message: error.message,
        description: error.description,
        context: error.context,
        type: error.type
      });
    });

    socket.on('error', (error) => {
      console.error('Socket error event:', error);
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Socket reconnection attempt:', attemptNumber);
    });

    socket.on('reconnect_failed', () => {
      console.error('Socket reconnection failed');
    });

    // Add a timeout to detect connection issues
    const connectionTimeout = setTimeout(() => {
      if (!socket.connected) {
        console.error('Socket connection timeout - trying to reconnect...');
        socket.connect();
      }
    }, 10000);

    // Request position update when the component mounts
    socket.emit('requestPositionUpdate');
    console.log('Emitted requestPositionUpdate');

    socket.on('positionUpdate', (data) => {
      setPositions(data?.positions || []);
    });

    socket.emit('requestAccountInfo');

    socket.on('accountBalanceUpdate', (data) => {
      setAccountInfo(data || {});
    });

    socket.on('statusUpdate', (data) => {
      
      const newStatus = {};
      Object.keys(data.collectedData || {}).forEach(asset => {
        const ohlcvArr = data.collectedData[asset];
        const lastPeriod = ohlcvArr[ohlcvArr.length - 1];
        newStatus[asset] = {
          ...lastPeriod,
          ohlcv: ohlcvArr,
        };
      });
      
      if (debugMode) {
        console.log('Status update received for assets:', Object.keys(newStatus));
        Object.keys(newStatus).forEach(asset => {
          const lastPeriod = newStatus[asset];
          console.log(`${asset}: close=${lastPeriod?.close}, open=${lastPeriod?.open}, high=${lastPeriod?.high}, low=${lastPeriod?.low}`);
        });
      }
      
      setStatus((prevStatus) => {
        // Check which assets actually have price changes
        Object.keys(newStatus).forEach(asset => {
          const oldPrice = prevStatus[asset]?.close;
          const newPrice = newStatus[asset]?.close;
          
          // Only trigger animation if price actually changed significantly
          if (oldPrice !== undefined && newPrice !== undefined) {
            // Use a small tolerance for floating-point comparison (0.00001)
            const priceChange = Math.abs(newPrice - oldPrice);
            const tolerance = 0.00001;
            
            if (priceChange > tolerance) {
              console.log(`🟢 Price change detected for ${asset}: ${oldPrice} → ${newPrice} (change: ${priceChange.toFixed(6)})`);
              handlePriceUpdate(asset);
            } else if (debugMode) {
              console.log(`No significant price change for ${asset}: ${oldPrice} → ${newPrice} (change: ${priceChange.toFixed(6)})`);
            }
          }
        });
        
        const updatedStatus = {
          ...prevStatus,
          ...newStatus
        };
        
        if (debugMode) {
          console.log('Updated status assets:', Object.keys(updatedStatus));
        }
        return updatedStatus;
      });
      
      if (data.account) {
        setAccountInfo(data.account);
      }
    });

    // Fetch additional real-time data
    fetchPerformanceData();
    fetchAIStats();
    fetchSignalAnalysis();
    fetchAssetAnalysis();
    generateAlerts();
    
    // Test server connection
    fetch(`${ENDPOINT}/api/health`)
      .then(response => response.json())
      .then(data => console.log('Server health check:', data))
      .catch(error => console.error('Server health check failed:', error));
    
    // Trigger initial asset analysis after a delay to ensure server is ready
    setTimeout(() => {
      console.log('Triggering initial asset analysis...');
      fetchAssetAnalysis();
    }, 5000); // Increased delay to reduce initial load

    // Set up periodic refresh for real-time data
    const performanceInterval = setInterval(fetchPerformanceData, 30000); // Every 30 seconds
    const aiStatsInterval = setInterval(fetchAIStats, 60000); // Every minute
    const signalInterval = setInterval(fetchSignalAnalysis, 15000); // Every 15 seconds
    const assetAnalysisInterval = setInterval(fetchAssetAnalysis, 60000); // Every 60 seconds (reduced frequency)
    const alertsInterval = setInterval(generateAlerts, 10000); // Every 10 seconds

    setIsLoading(false);

    return () => {
      console.log('Cleaning up socket connection...');
      clearTimeout(connectionTimeout);
      socket.disconnect();
      clearInterval(performanceInterval);
      clearInterval(aiStatsInterval);
      clearInterval(signalInterval);
      clearInterval(assetAnalysisInterval);
      clearInterval(alertsInterval);
    };
  }, []);

  const fetchPerformanceData = async () => {
    try {
      const response = await fetch(`${ENDPOINT}/api/performance`);
      const data = await response.json();
      setPerformanceData(data.metrics || data);
    } catch (error) {
      console.error('Error fetching performance data:', error);
    }
  };

  const fetchAIStats = async () => {
    try {
      const response = await fetch(`${ENDPOINT}/api/aiStats`);
      const data = await response.json();
      setAiStats(data);
    } catch (error) {
      console.error('Error fetching AI stats:', error);
    }
  };

  const fetchSignalAnalysis = async () => {
    try {
      let assets = Object.keys(status);
      
      // If no assets in status, use default assets or combined data assets
      if (assets.length === 0) {
        assets = Object.keys(combinedData);
        if (assets.length === 0) {
          // Fallback to common trading pairs
          // assets = ['EURUSD', 'USDJPY', 'GBPUSD', 'USDCAD', 'AUDUSD'];
          assets = [];
        }
      }
      
      console.log(`Fetching signal analysis for ${assets.length} assets:`, assets);
      
      const analysisPromises = assets.map(asset => 
        fetch(`${ENDPOINT}/api/signalAnalysis/${asset}`)
          .then(res => {
            if (!res.ok) {
              throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }
            return res.json();
          })
          .then(data => ({
            success: true,
            asset,
            analysis: data.analysis || data, // Handle different response formats
          }))
          .catch(err => {
            console.warn(`Failed to fetch signal analysis for ${asset}:`, err.message);
            // Return default signal data instead of failing
            return {
              success: true,
              asset,
              analysis: {
                signal: 'neutral',
                confidence: 0.5,
                recommendations: [`No signal data available for ${asset}`],
                timestamp: Date.now(),
                error: err.message
              }
            };
          })
      );
      
      const results = await Promise.all(analysisPromises);
      const analysis = {};
      results.forEach(result => {
        if (result.success && result.analysis) {
          // Ensure signal has required properties
          const signalData = {
            signal: result.analysis.signal || 'neutral',
            confidence: result.analysis.confidence || 0.5,
            recommendations: result.analysis.recommendations || ['No recommendations available'],
            timestamp: result.analysis.timestamp || Date.now(),
            ...result.analysis
          };
          analysis[result.asset] = signalData;
        }
      });
      
      console.log(`Signal analysis updated for ${Object.keys(analysis).length} assets`);
      setSignalAnalysis(analysis);
    } catch (error) {
      console.error('Error fetching signal analysis:', error);
      // Set default signal analysis for current assets to prevent undefined
      const defaultAnalysis = {};
      const currentAssets = Object.keys(status).length > 0 ? Object.keys(status) : Object.keys(combinedData);
      currentAssets.forEach(asset => {
        defaultAnalysis[asset] = {
          signal: 'neutral',
          confidence: 0.5,
          recommendations: ['Signal analysis temporarily unavailable'],
          timestamp: Date.now(),
          error: 'Failed to fetch signal analysis'
        };
      });
      setSignalAnalysis(defaultAnalysis);
    }
  };

  const fetchAssetAnalysis = async () => {
    try {
      let assets = Object.keys(status);
      
      // If no assets in status, use default assets for testing
      if (assets.length === 0) {
        console.log('No assets in status, using default assets for testing');
        assets = [];
        // assets = ['EURUSD', 'USDJPY', 'GBPUSD', 'USDCAD'];
      }
      
      // Only fetch analysis for assets that have data and haven't been analyzed recently
      const currentTime = Date.now();
      const analysisCache = assetAnalysis;
      const assetsToAnalyze = assets.filter(asset => {
        const lastAnalysis = analysisCache[asset]?.timestamp || 0;
        return (currentTime - lastAnalysis) > 30000; // Only analyze if > 30 seconds old
      });
      
      if (assetsToAnalyze.length === 0) {
        console.log('All assets recently analyzed, skipping fetch');
        return;
      }
      
      console.log(`Fetching asset analysis for ${assetsToAnalyze.length} assets:`, assetsToAnalyze);
      
      const analysisPromises = assetsToAnalyze.map(asset => 
        fetch(`${ENDPOINT}/api/assetAnalysis?asset=${asset}`)
          .then(res => res.json())
          .then(data => ({ ...data, timestamp: currentTime }))
          .catch(err => ({ success: false, asset, error: err.message, timestamp: currentTime }))
      );
      
      const results = await Promise.all(analysisPromises);
      const newAnalysis = {};
      results.forEach(result => {
        if (result.success) {
          newAnalysis[result.asset] = result;
        } else {
          console.log(`Failed to fetch analysis for ${result.asset}:`, result.error);
        }
      });
      
      // Merge with existing analysis
      setAssetAnalysis(prevAnalysis => ({
        ...prevAnalysis,
        ...newAnalysis
      }));
      
      console.log(`Asset analysis updated for ${Object.keys(newAnalysis).length} assets`);
    } catch (error) {
      console.error('Error fetching asset analysis:', error);
    }
  };

  const generateAlerts = () => {
    const newAlerts = [];
    const now = Date.now();

    // Check for strong signals
    Object.entries(signalAnalysis).forEach(([asset, analysis]) => {
      if (analysis.signal.includes('strong') && analysis.confidence > 0.8) {
        newAlerts.push({
          id: `signal-${asset}-${now}`,
          type: 'signal',
          asset,
          message: `Strong ${analysis.signal.replace('_', ' ')} signal for ${asset} (${(analysis.confidence * 100).toFixed(0)}% confidence)`,
          timestamp: now,
          priority: 'high'
        });
      }
    });

    // Check for significant price movements
    Object.entries(combinedData).forEach(([asset, data]) => {
      if (data.open && data.close) {
        const priceChange = ((data.close - data.open) / data.open) * 100;
        if (Math.abs(priceChange) > 0.5) {
          newAlerts.push({
            id: `price-${asset}-${now}`,
            type: 'price',
            asset,
            message: `${asset} price ${priceChange > 0 ? 'increased' : 'decreased'} by ${Math.abs(priceChange).toFixed(2)}%`,
            timestamp: now,
            priority: priceChange > 1 ? 'high' : 'medium'
          });
        }
      }
    });

    // Check for performance alerts
    if (performanceData.winRate && parseFloat(performanceData.winRate) < 40) {
      newAlerts.push({
        id: `performance-${now}`,
        type: 'performance',
        message: `Low win rate: ${performanceData.winRate}% - Consider strategy review`,
        timestamp: now,
        priority: 'high'
      });
    }

    // Keep only recent alerts (last 5 minutes)
    const fiveMinutesAgo = now - (5 * 60 * 1000);
    setAlerts(prevAlerts => {
      const filtered = prevAlerts.filter(alert => alert.timestamp > fiveMinutesAgo);
      return [...filtered, ...newAlerts].slice(-10); // Keep max 10 alerts
    });
  };

  // Blinking/row animation removed

  useEffect(() => {
    console.log('useEffect triggered - status keys:', Object.keys(status), 'positions length:', positions.length);
    
    // Combine status and positions data
    const combined = {};
    Object.keys(status).forEach(asset => {
      combined[asset] = {
        ...status[asset],
        positions: positions.filter(pos => pos && pos.market && pos.market.epic === asset) || []
      };
    });
    
    setCombinedData(combined);
    
    // Trigger asset analysis and signal analysis when status changes
    if (Object.keys(status).length > 0) {
      console.log('Status changed, triggering asset and signal analysis...');
      fetchAssetAnalysis();
      fetchSignalAnalysis();
    }
  }, [status, positions]);

  const [priceUpdateTimes, setPriceUpdateTimes] = useState({});

  const handlePriceUpdate = (asset) => {
    const now = Date.now();
    const lastUpdate = priceUpdateTimes[asset];
    
    // Prevent rapid successive updates (debounce)
    if (lastUpdate && (now - lastUpdate) < 1000) {
      console.log(`Skipping price update for ${asset} - too recent (${now - lastUpdate}ms ago)`);
      return;
    }
    
    console.log(`Handling price update for ${asset} at ${new Date(now).toLocaleTimeString()}`);
    
    setPriceUpdateTimes(prev => ({
      ...prev,
      [asset]: now
    }));
    
    // Remove the highlight after 2 seconds
    setTimeout(() => {
      setPriceUpdateTimes(prev => {
        const newTimes = { ...prev };
        delete newTimes[asset];
        return newTimes;
      });
    }, 2000);
  };

  const getSignalColor = (signal) => {
    switch (signal) {
      case 'strong_buy': return 'signal-strong-buy';
      case 'buy': return 'signal-buy';
      case 'weak_buy': return 'signal-weak-buy';
      case 'strong_sell': return 'signal-strong-sell';
      case 'sell': return 'signal-sell';
      case 'weak_sell': return 'signal-weak-sell';
      default: return 'signal-neutral';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.6) return 'confidence-medium';
    return 'confidence-low';
  };

  const getVolatilityLevel = (volatility) => {
    if (volatility > 2) return 'high';
    if (volatility > 1) return 'medium';
    return 'low';
  };

  const getRiskLevel = (leverage, volatility) => {
    const riskScore = leverage * (volatility / 100);
    if (riskScore > 0.5) return 'high';
    if (riskScore > 0.2) return 'medium';
    return 'low';
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };



  const renderTable = (asset, index) => {
    const data = combinedData[asset];
    
    // Check if data exists, if not return a loading row
    if (!data) {
      return (
        <tr key={asset} className="data-row loading-row">
          <td className="data-cell data-cell-bold">
            <div className="asset-info">
              <div className="asset-name">{index + 1}. {asset}</div>
              <div className="loading-indicator">Loading...</div>
            </div>
          </td>
          <td colSpan="8" className="data-cell">
            <div className="loading-message">Data loading for {asset}...</div>
          </td>
        </tr>
      );
    }
    
    const contract = data.positions || [];
    
    // Ensure signal always has valid data with fallback
    const signal = signalAnalysis[asset] || {
      signal: 'neutral',
      confidence: 0.5,
      recommendations: ['Signal analysis loading...'],
      timestamp: Date.now(),
      loading: true
    };
    
    const analysis = assetAnalysis[asset];
    const isPriceUpdated = priceUpdateTimes[asset];
    
    // Debug logging for price updates
    if (debugMode && isPriceUpdated) {
      console.log(`🔴 Price update detected for ${asset} at ${new Date(isPriceUpdated).toLocaleTimeString()}`);
    }

    // Calculate price change and percentage
    const priceChange = data.open && data.close ? data.close - data.open : 0;
    const priceChangePercent = data.open ? (priceChange / data.open) * 100 : 0;
    const priceChangeClass = priceChange >= 0 ? 'positive' : 'negative';

    // Calculate volatility (high-low range)
    const volatility = data.high && data.low ? ((data.high - data.low) / data.low) * 100 : 0;
    const volatilityLevel = getVolatilityLevel(volatility);

    // Determine trend direction
    const getTrendDirection = () => {
      if (!data.open || !data.close) return 'neutral';
      const change = ((data.close - data.open) / data.open) * 100;
      if (change > 0.1) return 'up';
      if (change < -0.1) return 'down';
      return 'neutral';
    };

    const trendDirection = getTrendDirection();

    // Calculate total position value and P&L
    const totalPositionValue = contract.reduce((sum, pos) => {
      if (!pos || !pos.position) return sum;
      return sum + (pos.position.size * (data.close || 0));
    }, 0);
    const totalUPL = contract.reduce((sum, pos) => {
      if (!pos || !pos.position) return sum;
      return sum + (pos.position.upl || 0);
    }, 0);
    
    // Calculate average leverage and risk level
    const avgLeverage = contract.length > 0 ? 
      contract.reduce((sum, pos) => {
        if (!pos || !pos.position) return sum;
        return sum + (pos.position.leverage || 0);
      }, 0) / contract.length : 0;
    const riskLevel = getRiskLevel(avgLeverage, volatility);

    // Calculate volume if available
    const volume = data.volume || 0;
    const volumeFormatted = volume > 1000000 ? `${(volume / 1000000).toFixed(1)}M` : 
                           volume > 1000 ? `${(volume / 1000).toFixed(1)}K` : volume.toString();

    // Prepare OHLCV data for the mini chart (last 100 points)
    let ohlcv = [];
    if (status[asset] && Array.isArray(status[asset].ohlcv)) {
      ohlcv = status[asset].ohlcv.slice(-100).map(candle => ({
        time: candle.time
          ? (typeof candle.time === 'number' && candle.time > 1000000000000 ? Math.floor(candle.time / 1000) : candle.time)
          : (candle.date ? (typeof candle.date === 'number' && candle.date > 1000000000000 ? Math.floor(candle.date / 1000) : candle.date) : undefined),
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      })).filter(candle => candle.time !== undefined);
    }

    return (
      <tr key={asset} className={`data-row ${debugMode && isPriceUpdated ? 'debug-highlight' : ''}`}>
        {/* Asset Column */}
        <td className="data-cell data-cell-bold">
          <div className="asset-info">
            <div className="asset-name">{index + 1}. {asset}</div>
            <div className={`market-status-dot ${isPriceUpdated ? 'dot-blink' : ''}`}>
              <span className={`status-dot ${analysis?.marketStatus === 'TRADEABLE' || analysis?.marketStatus?.status === 'OPEN' ? 'open' : 'closed'}`}></span>
              <span className="status-text">
                {analysis?.marketStatus ? (
                  typeof analysis.marketStatus === 'object' ? analysis.marketStatus.status : analysis.marketStatus
                ) : 'UNKNOWN'}
              </span>
            </div>
          </div>
        </td>
        
        {/* Signal & Confidence Column */}
        <td className="data-cell">
          <div className="signal-confidence-container">
            <div className="signal-indicator">
              <span className={`signal-badge ${getSignalColor(signal.signal)} ${signal.loading ? 'signal-loading' : ''}`}>
                {signal.loading ? 'LOADING...' : signal.signal.replace('_', ' ').toUpperCase()}
              </span>
              {signal.error && (
                <div className="signal-error" title={signal.error}>⚠️</div>
              )}
            </div>
            <DualConfidenceBar 
              signalConfidence={signal.confidence}
              aiConfidence={analysis?.aiFeedback?.confidence || 0.5}
            />
          </div>
        </td>
        
        {/* Price & Change Column */}
        <td className="data-cell">
          <div className="price-info">
            <div className="current-price">{data.close?.toFixed(5) || 'N/A'}</div>
            <div className={`price-change ${priceChangeClass}`}>
              {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(5)} ({priceChange >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%)
            </div>
            <div className="volatility-trend">
              <span className={`volatility-value volatility-${volatilityLevel}`}>
                Vol: {volatility.toFixed(2)}%
              </span>
              <span className={`trend-indicator trend-${trendDirection}`}>
                {trendDirection === 'up' ? '↗' : trendDirection === 'down' ? '↘' : '→'}
              </span>
            </div>
          </div>
        </td>

        {/* Chart & Trend Column */}
        <td className="data-cell">
          <div className="chart-container">
            {ohlcv.length > 0 ? (
              <MiniCandleChart key={asset} ohlcv={ohlcv} asset={asset} />
            ) : (
              <div style={{ width: 200, height: 60, background: '#222', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
                No Chart Data
              </div>
            )}
            {volume > 0 && (
              <div className="volume-info">Vol: {volumeFormatted}</div>
            )}
          </div>
        </td>

        {/* Position & P&L Column */}
        <td className="data-cell">
          <div className="position-pnl-info">
            {contract.length > 0 ? (
              <>
                <div className="position-summary">
                  <span className="position-count">{contract.length} pos</span>
                  <span className="total-size">{formatCurrency(totalPositionValue)}</span>
                </div>
                <div className={`total-pnl ${totalUPL >= 0 ? 'positive' : 'negative'}`}>
                  {totalUPL >= 0 ? '+' : ''}{totalUPL.toFixed(2)} ({data.close ? ((totalUPL / totalPositionValue) * 100).toFixed(1) : '0'}%)
                </div>
                <div className="leverage-info">
                  Avg: {avgLeverage.toFixed(1)}x leverage
                </div>
              </>
            ) : (
              <div className="no-position">No positions</div>
            )}
          </div>
        </td>

        {/* Risk & Status Column */}
        <td className="data-cell">
          <div className="risk-status-info">
            <div className={`risk-badge risk-${riskLevel}`}>
              {riskLevel.toUpperCase()} RISK
            </div>
            {contract.length > 0 && (
              <div className="risk-levels">
                <div className="stop-loss">SL: {contract[0].position.stopLevel?.toFixed(5) || 'N/A'}</div>
                <div className="take-profit">TP: {contract[0].position.profitLevel?.toFixed(5) || 'N/A'}</div>
              </div>
            )}
            {analysis?.aiFeedback && (
              <div className="ai-decision-compact">
                <span className={`decision-badge decision-${analysis.aiFeedback.decision}`}>
                  AI: {analysis.aiFeedback.decision.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </td>

        {/* Actions Column */}
        <td className="data-cell data-cell-actions">
          <div className="action-buttons">
            <button className="button info" onClick={(e) => { 
              e.stopPropagation(); 
              let info = `${asset} Analysis\n\n`;
              
              // Signal Info
              info += `Signal: ${signal.signal.toUpperCase()}\nSignal Confidence: ${(signal.confidence * 100).toFixed(1)}%\n`;
              if (signal.recommendations) {
                info += `\nSignal Recommendations:\n${signal.recommendations.join('\n')}\n`;
              }
              
              // AI Info
              if (analysis?.aiFeedback) {
                const feedback = analysis.aiFeedback;
                info += `\nAI Decision: ${feedback.decision.toUpperCase()}\nAI Confidence: ${(feedback.confidence * 100).toFixed(1)}%\n\nAI Reasoning:\n${feedback.reasoning}\n`;
                if (feedback.recommendations) {
                  info += `\nAI Recommendations:\n${feedback.recommendations.join('\n')}\n`;
                }
              }
              
              if (signal.error) {
                info += `\nError: ${signal.error}`;
              }
              
              alert(info);
            }}>
              Details
            </button>
          </div>
        </td>
      </tr>
    )
  };

  // Separate positions with and without positionId
  const positionsWithContracts = Object.keys(combinedData).filter(asset => combinedData[asset].positions.length > 0).sort();
  const positionsWithoutContracts = Object.keys(combinedData).filter(asset => combinedData[asset].positions.length === 0).sort();

  // Concatenate the sorted groups, with positions having positionId on top
  let sortedPositions = [...positionsWithContracts, ...positionsWithoutContracts];
  
  // If no positions at all, show all assets from status data
  if (sortedPositions.length === 0 && Object.keys(status).length > 0) {
    console.log('No positions found, showing all assets from status data');
    sortedPositions = Object.keys(status).sort();
  }
  
  // Show a message if no positions are found
  if (sortedPositions.length === 0) {
    console.log('No trading positions found. This is normal if you have no open trades.');
  }
  
  // Function to determine the CSS class based on the value
  const getAmountClass = (amount) => {
    return amount < 0 ? 'negative' : 'positive';
  };

  if (isLoading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <ChatTradeLogo className="logo" />
          </div>
          <div className="nav-section">
            <Link to="/backtest" className="nav-link" style={{ color: '#027bff', textDecoration: 'none', marginRight: '20px', fontSize: '16px', fontWeight: 'bold' }}>
              Backtesting
            </Link>
            <button 
              className="debug-toggle"
              onClick={() => setDebugMode(!debugMode)}
              style={{
                backgroundColor: debugMode ? '#027bff' : '#333',
                color: '#ffffff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Debug: {debugMode ? 'ON' : 'OFF'}
            </button>
            <button 
              className="confidence-toggle"
              onClick={() => setShowFloatingPanel(!showFloatingPanel)}
              style={{
                backgroundColor: showFloatingPanel ? '#027bff' : '#333',
                color: '#ffffff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              📊 Confidence Monitor
            </button>
          </div>
        </div>
      </header>

      {/* Performance Metrics Dashboard */}
      <div className="performance-dashboard">
        <div className="performance-grid">
          <div className="performance-card">
            <h3>Performance Metrics</h3>
            <div className="metrics-grid">
              <div className="metric">
                <span className="metric-label">Win Rate</span>
                <span className="metric-value">{performanceData.winRate || '0'}%</span>
              </div>
              <div className="metric">
                <span className="metric-label">Total Trades</span>
                <span className="metric-value">{performanceData.totalTrades || '0'}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Profit Factor</span>
                <span className="metric-value">{performanceData.profitFactor || '0'}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Max Drawdown</span>
                <span className="metric-value negative">{performanceData.maxDrawdown || '0'}%</span>
              </div>
              <div className="metric">
                <span className="metric-label">Sharpe Ratio</span>
                <span className="metric-value">{performanceData.sharpeRatio || '0'}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Total P&L</span>
                <span className={`metric-value ${getAmountClass(performanceData.totalProfit)}`}>
                  {accountInfo.currencySymbol} {performanceData.totalProfit || '0'}
                </span>
              </div>
            </div>
          </div>

          <div className="performance-card">
            <h3>AI Model Performance</h3>
            <div className="ai-stats">
              {aiStats.mlStats ? (
                <div className="ai-metrics">
                  <div className="ai-metric">
                    <span className="metric-label">Total Decisions</span>
                    <span className="metric-value">{aiStats.mlStats.totalDecisions || '0'}</span>
                  </div>
                  <div className="ai-metric">
                    <span className="metric-label">Avg Confidence</span>
                    <span className="metric-value">{((aiStats.mlStats.averageConfidence || 0) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="ai-metric">
                    <span className="metric-label">Recent Decisions</span>
                    <span className="metric-value">{aiStats.mlStats.recentDecisions?.length || '0'}</span>
                  </div>
                </div>
              ) : (
                <div className="ai-loading">Loading AI stats...</div>
              )}
            </div>
          </div>

          <div className="performance-card">
            <h3>Market Overview</h3>
            <div className="market-overview">
              <div className="market-stat">
                <span className="stat-label">Active Signals</span>
                <span className="stat-value">
                  {Object.values(signalAnalysis).filter(s => s.signal.includes('buy') || s.signal.includes('sell')).length}
                </span>
              </div>
              <div className="market-stat">
                <span className="stat-label">Strong Signals</span>
                <span className="stat-value">
                  {Object.values(signalAnalysis).filter(s => s.signal.includes('strong')).length}
                </span>
              </div>
              <div className="market-stat">
                <span className="stat-label">Open Positions</span>
                <span className="stat-value">{positionsWithContracts.length}</span>
              </div>
            </div>
          </div>

          <div className="performance-card">
            <h3>Account Information</h3>
            <div className="account-overview">
              <div className="account-stat">
                <span className="stat-label">Balance</span>
                <span className={`stat-value ${getAmountClass(accountInfo.balance)}`}>
                  {accountInfo.currencySymbol} {accountInfo.balance?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="account-stat">
                <span className="stat-label">Available</span>
                <span className={`stat-value ${getAmountClass(accountInfo.available)}`}>
                  {accountInfo.currencySymbol} {accountInfo.available?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="account-stat">
                <span className="stat-label">Profit/Loss</span>
                <span className={`stat-value ${getAmountClass(accountInfo.profitLoss)}`}>
                  {accountInfo.currencySymbol} {accountInfo.profitLoss?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="account-stat">
                <span className="stat-label">Account Type</span>
                <span className="stat-value">
                  {accountInfo.accountType || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Alerts */}
      {alerts.length > 0 && (
        <div className="alerts-section">
          <h3>Real-time Alerts</h3>
          <div className="alerts-container">
            {alerts.map(alert => (
              <div key={alert.id} className={`alert alert-${alert.priority} alert-${alert.type}`}>
                <span className="alert-message">{alert.message}</span>
                <span className="alert-time">
                  {new Date(alert.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <table className="styled-table" ref={tableRef}>
        <thead>
          <tr>
            <th className="header-cell">Asset</th>
            <th className="header-cell">Signal & Confidence</th>
            <th className="header-cell">Price & Change</th>
            <th className="header-cell">Chart & Trend</th>
            <th className="header-cell">Position & P&L</th>
            <th className="header-cell">Risk & Status</th>
            <th className="header-cell">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedPositions.map((asset, index) => {
            return renderTable(asset, index)
          })}
          {/* Portfolio Summary Row */}
          <tr className="portfolio-summary">
            <td className="data-cell data-cell-bold">
              <div className="summary-label">PORTFOLIO SUMMARY</div>
            </td>
            <td className="data-cell">
              <div style={{ textAlign: "center", color: "#888", fontSize: 10 }}>
                AVG CONFIDENCE
              </div>
            </td>
            <td className="data-cell">
              <div className="summary-info">
                <div className="summary-value">
                  {formatCurrency(Object.values(combinedData).reduce((sum, data) => {
                    if (!data || !data.positions) return sum;
                    const totalValue = data.positions.reduce((posSum, pos) => {
                      if (!pos || !pos.position) return posSum;
                      return posSum + (pos.position.size * (data.close || 0));
                    }, 0);
                    return sum + totalValue;
                  }, 0))}
                </div>
                <div className="summary-label">Total Value</div>
              </div>
            </td>
            <td className="data-cell">
              <div style={{ textAlign: "center", color: "#888", fontSize: 10 }}>
                OVERALL TREND
              </div>
            </td>
            <td className="data-cell">
              <div className="summary-info">
                <div className="summary-value">
                  {Object.values(combinedData).reduce((sum, data) => sum + (data?.positions?.length || 0), 0)} positions
                </div>
                <div className="summary-value">
                  {Object.values(combinedData).reduce((sum, data) => {
                    if (!data || !data.positions) return sum;
                    const totalUPL = data.positions.reduce((posSum, pos) => {
                      if (!pos || !pos.position) return posSum;
                      return posSum + (pos.position.upl || 0);
                    }, 0);
                    return sum + totalUPL;
                  }, 0).toFixed(2)} P&L
                </div>
              </div>
            </td>
            <td className="data-cell">
              <div className="summary-info">
                <div className="summary-value">
                  {Object.values(combinedData).reduce((sum, data) => sum + (data?.positions?.length || 0), 0) > 0 ? 
                    (Object.values(combinedData).reduce((sum, data) => {
                      if (!data || !data.positions) return sum;
                      const avgLeverage = data.positions.length > 0 ? 
                        data.positions.reduce((posSum, pos) => {
                          if (!pos || !pos.position) return posSum;
                          return posSum + (pos.position.leverage || 0);
                        }, 0) / data.positions.length : 0;
                      return sum + avgLeverage;
                    }, 0) / Object.values(combinedData).reduce((sum, data) => sum + (data?.positions?.length || 0), 0)).toFixed(1) : '0'}x
                </div>
                <div className="summary-label">Avg Leverage</div>
              </div>
            </td>
            <td className="data-cell"></td>
          </tr>
        </tbody>
      </table>

      {/* Floating Confidence Panel */}
      {showFloatingPanel && (
        <FloatingConfidencePanel 
          signalData={signalAnalysis} 
          onClose={() => setShowFloatingPanel(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;